import { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router";
import jwt from "jsonwebtoken";
import { ApolloClient, OperationVariables } from "@apollo/client";
import { useLazyQuery, useMutation, useApolloClient } from "@apollo/client/react";
import authService, { SET_AUTH } from "../../services/authService";
import * as MFATypes from "../../model/MFATypes";
import AppContext from "../../context/AppContext";
import {
  getBiometricCapability,
  enrolBiometric,
  signBiometricChallenge,
  isFlutterBridgeAvailable,
  getBiometricLabel,
} from "../../services/mobilityService";
import {
  GetBiometricProviderQuery,
  RegisterBiometricDeviceMutation,
  GetBiometricChallengeMutation,
  VerifyBiometricAssertionMutation,
  GetBiometricLoginChallengeMutation,
  VerifyBiometricLoginMutation,
  DisableBiometricMutation,
} from "./BiometricQueries";
import { storeMobileDeviceToken } from "../LoginPage/storeMobileDeviceToken";
import {
  UpdateUserDeviceSettings,
  UpdateUserDeviceSettingsVariables,
} from "../LoginPage/types/UpdateUserDeviceSettings";
import UpdateUserDeviceSettingsMutation from "../LoginPage/UpdateUserDeviceSettingsMutation";
import { getCpdProviders } from "../LoginPage/types/getCpdProviders";
import GetCpdProvidersQuery from "../LoginPage/GetCpdProvidersQuery";
import { GetOptions } from "../App/types/GetOptions";
import GetOptionsQuery from "../App/GetOptionsQuery";

/**
 * Shared biometric (Face ID / fingerprint) orchestration for the mobile
 * wrapper. Centralises the three sub-flows so they can be reused across screens:
 *
 *  - offerEnable():  first-login "Enable biometrics?" prompt (patient list page)
 *  - enable():       enrol the device key + register it server-side
 *  - verify():       returning-user challenge → sign → verify → session token
 *
 * All operations are safe no-ops outside the Flutter shell.
 */
export const useBiometric = () => {
  const navigate = useNavigate();
  const client: ApolloClient = useApolloClient();
  const isDesktop = useContext(AppContext).isDesktop;

  // Only active inside the native shell.
  const mobility = authService.isMobility() && isFlutterBridgeAvailable();

  const [promptOpen, setPromptOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [methodName, setMethodName] = useState("Biometrics");
  const [loading, setLoading] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const providerIdRef = useRef<number | null>(null);
  const pendingNavigateRef = useRef<string | null>(null);

  const [getBiometricProvider] = useLazyQuery<any, any>(GetBiometricProviderQuery, { fetchPolicy: "no-cache" });
  const [registerBiometricDevice] = useMutation<any, any>(RegisterBiometricDeviceMutation);
  const [getBiometricChallengeMutation] = useMutation<any, any>(GetBiometricChallengeMutation, {
    fetchPolicy: "no-cache",
  });
  const [verifyBiometricAssertion] = useMutation<any, any>(VerifyBiometricAssertionMutation);

  // Unauthenticated cold-launch login variants (no prior session/token).
  const [getBiometricLoginChallenge] = useMutation<any, any>(GetBiometricLoginChallengeMutation, {
    fetchPolicy: "no-cache",
  });
  const [verifyBiometricLogin] = useMutation<any, any>(VerifyBiometricLoginMutation);
  const [disableBiometricMutation] = useMutation<any, any>(DisableBiometricMutation);

  const [updateUsersDeviceConfig] = useMutation<UpdateUserDeviceSettings, UpdateUserDeviceSettingsVariables>(
    UpdateUserDeviceSettingsMutation,
  );
  const [cpdProviders] = useLazyQuery<getCpdProviders, OperationVariables>(GetCpdProvidersQuery);
  const [getOptions] = useLazyQuery<GetOptions, Record<string, never>>(GetOptionsQuery);

  /**
   * Decide whether to show the "enable biometrics" prompt for the current user.
   * Returns true when the prompt was opened. Safe to call on any screen; it
   * no-ops outside mobility, when already enabled, or when previously asked.
   */
  const offerEnable = async (destination?: string): Promise<boolean> => {
    if (!mobility) return false;

    // Biometric enrolment is not available to SSO/Cognito sign-ins (server-side
    // registration is disallowed for Cognito users), so never auto-offer it.
    if (authService.isCognito()) return false;

    const username = authService.getUsername();
    if (!username) return false;

    try {
      const capability = await getBiometricCapability();
      // The offer is driven by DEVICE capability ("available on this device").
      if (!capability.supported || !capability.platform) return false;

      // Fetch the provider keyed by this device. `deviceEnrolled` tells us
      // whether the server still has a usable key for this device, letting us
      // reconcile a stale local flag (e.g. the key was revoked server-side or
      // the table was reset) rather than silently trusting localStorage.
      let deviceEnrolled = false;
      try {
        const res = await getBiometricProvider({ variables: { deviceId: capability.deviceId } });
        const provider = res?.data?.getBiometricProvider;
        providerIdRef.current = provider?.available && provider?.id ? provider.id : null;
        deviceEnrolled = provider?.deviceEnrolled === true;
      } catch {
        providerIdRef.current = null;
      }

      // Self-heal a desync: if we locally believe biometrics are enabled but
      // the server has no key for this device, clear the stale local state so
      // the user is offered enrolment again.
      if (authService.getBiometricEnabled(username) && !deviceEnrolled) {
        authService.setBiometricEnabled(username, false);
        authService.setBiometricPrompted(username, false);
      }

      // Don't offer again if this device is genuinely enrolled, or the user has
      // already been asked this session/relaunch.
      if (
        (authService.getBiometricEnabled(username) && deviceEnrolled) ||
        authService.getBiometricPrompted(username)
      ) {
        return false;
      }

      setMethodName(getBiometricLabel(capability));
      pendingNavigateRef.current = destination || null;
      setPromptOpen(true);
      return true;
    } catch (e) {
      console.error("useBiometric.offerEnable failed", e);
      return false;
    }
  };

  /**
   * Open the enable-biometrics prompt on demand (e.g. from a menu action),
   * regardless of whether the user was already prompted this session. Unlike
   * offerEnable(), this does not gate on prompted/enabled state — the user
   * explicitly asked for it. Returns false (and does not open) only when the
   * device can't support biometrics. enable()/decline() drive the outcome, the
   * same as the auto-offer flow on the patient list.
   */
  const openEnrolPrompt = async (destination?: string): Promise<boolean> => {
    if (!mobility) return false;

    // Biometric enrolment is not available to SSO/Cognito sign-ins (server-side
    // registration is disallowed for Cognito users), so the manual menu entry
    // must not open the prompt either.
    if (authService.isCognito()) return false;

    try {
      const capability = await getBiometricCapability();
      if (!capability.supported || !capability.platform) return false;

      // Best-effort provider pre-capture; enable() re-fetches if this is null.
      try {
        const res = await getBiometricProvider();
        const provider = res?.data?.getBiometricProvider;
        providerIdRef.current = provider?.available && provider?.id ? provider.id : null;
      } catch {
        providerIdRef.current = null;
      }

      setMethodName(getBiometricLabel(capability));
      pendingNavigateRef.current = destination || null;
      setPromptOpen(true);
      return true;
    } catch (e) {
      console.error("useBiometric.openEnrolPrompt failed", e);
      return false;
    }
  };

  /** User accepted the prompt: enrol the device key and register it. */
  const enable = async (): Promise<void> => {
    const username = authService.getUsername();
    setLoading(true);
    let enrolled = false;
    try {
      const capability = await getBiometricCapability();

      // If the provider wasn't captured during offerEnable (slow/failed fetch),
      // try once more now — enrolment genuinely needs a configured provider.
      if (!providerIdRef.current) {
        try {
          const res = await getBiometricProvider();
          const provider = res?.data?.getBiometricProvider;
          providerIdRef.current = provider?.available && provider?.id ? provider.id : null;
        } catch {
          providerIdRef.current = null;
        }
      }

      const enrol = await enrolBiometric();

      if (enrol.success && enrol.publicKey && providerIdRef.current) {
        const result = await registerBiometricDevice({
          variables: {
            input: {
              deviceId: enrol.deviceId || capability.deviceId,
              platform: enrol.platform || capability.platform,
              publicKey: enrol.publicKey,
            },
          },
        });

        if (result?.data?.registerBiometricDevice?.success) {
          authService.setBiometricEnabled(username, true);
          enrolled = true;
        }
      }
    } catch (e) {
      console.error("useBiometric.enable failed", e);
    } finally {
      // Mark as prompted regardless of outcome so we don't nag again.
      authService.setBiometricPrompted(username, true);
      setLoading(false);
      setPromptOpen(false);
      // Confirm a successful enrolment back to the user.
      if (enrolled) setSuccessOpen(true);
      const dest = pendingNavigateRef.current;
      pendingNavigateRef.current = null;
      if (dest) navigate(dest);
    }
  };

  /** Dismiss the enrolment-success confirmation. */
  const dismissSuccess = (): void => {
    setSuccessOpen(false);
  };

  /**
   * Resolve and cache the device's biometric label (e.g. "Face ID" /
   * "Fingerprint") into methodName. Useful before showing a dialog (like the
   * disable confirmation) that isn't preceded by an enrol/verify call — those
   * flows set methodName themselves, but a standalone dialog would otherwise
   * show the default "Biometrics".
   */
  const refreshMethodName = async (): Promise<void> => {
    if (!mobility) return;
    try {
      const capability = await getBiometricCapability();
      if (capability.supported && capability.platform) {
        setMethodName(getBiometricLabel(capability));
      }
    } catch {
      // Leave the current methodName as-is on failure.
    }
  };

  /**
   * Turn off biometric sign-in for this device: revoke the device key
   * server-side (so the cold-launch fast-path can no longer authenticate) and
   * clear the local "enabled" flag. Resets the prompted flag so the user can be
   * offered enrolment again later if they choose. Returns true on success.
   */
  const disable = async (): Promise<boolean> => {
    if (!mobility) return false;

    const username = authService.getUsername();
    setDisabling(true);
    try {
      const capability = await getBiometricCapability();

      // Scope the revoke to this device when we can resolve its id; otherwise
      // fall back to revoking all of the user's keys (deviceId omitted).
      const deviceId = capability?.deviceId;

      const result = await disableBiometricMutation({
        variables: { input: { deviceId: deviceId || null } },
      });

      if (result?.data?.disableBiometric?.success) {
        if (username) {
          // Clears the enabled flag and the remembered biometric username, and
          // re-opens the door to a future enable offer.
          authService.setBiometricEnabled(username, false);
          authService.setBiometricPrompted(username, false);
        }
        // Also clear for the account that most recently enrolled on this
        // device, in case it differs from the current session username.
        const enrolledUser = authService.getBiometricUsername();
        if (enrolledUser && enrolledUser.toUpperCase() !== (username || "").toUpperCase()) {
          authService.setBiometricEnabled(enrolledUser, false);
          authService.setBiometricPrompted(enrolledUser, false);
        }
        return true;
      }
      return false;
    } catch (e) {
      console.error("useBiometric.disable failed", e);
      return false;
    } finally {
      setDisabling(false);
    }
  };

  /** User declined the prompt: remember and continue. */
  const decline = (): void => {
    const username = authService.getUsername();
    authService.setBiometricPrompted(username, true);
    setPromptOpen(false);
    const dest = pendingNavigateRef.current;
    pendingNavigateRef.current = null;
    if (dest) navigate(dest);
  };

  /**
   * Run the challenge → sign → verify round-trip. On success, persists the
   * server-issued (fully otpVerified) access token — establishing a real
   * session that bypasses login + MFA — and navigates into the app.
   *
   * Returns true on success (navigation performed), false otherwise so the
   * caller can fall back to manual login / OTP.
   *
   * @param launch when true, runs the unauthenticated cold-launch login path
   *   (no prior session/token) — resolves the user from the device alone and
   *   bypasses login + MFA. When false (default), runs the in-session path
   *   (MFA page after password login).
   */
  const verify = async (destination?: string, launch = false): Promise<boolean> => {
    if (!mobility) return false;

    try {
      setVerifying(true);

      const capability = await getBiometricCapability();
      if (!capability.supported || !capability.platform || !capability.deviceId) return false;

      setMethodName(getBiometricLabel(capability));

      let challenge: string | undefined;

      if (launch) {
        // COLD-LAUNCH (no prior session/token): use the unauthenticated login
        // variants. The server resolves the user from the device alone, so we
        // skip the provider lookup (which is @Authorized and needs a token).
        const challengeRes = await getBiometricLoginChallenge({
          variables: { input: { deviceId: capability.deviceId } },
        });
        challenge = challengeRes?.data?.getBiometricLoginChallenge?.challenge;
      } else {
        // In-session path (e.g. MFA page after a password login). Uses the
        // authenticated provider + challenge mutations.
        const providerRes = await getBiometricProvider();
        const provider = providerRes?.data?.getBiometricProvider;
        if (!provider || !provider.available || !provider.id) return false;

        const challengeRes = await getBiometricChallengeMutation({
          variables: { input: { deviceId: capability.deviceId } },
        });
        challenge = challengeRes?.data?.getBiometricChallenge?.challenge;
      }

      if (!challenge) return false;

      // Device signs it (prompts Face ID / fingerprint).
      const signed = await signBiometricChallenge(challenge);
      if (!signed.success || !signed.signature) return false;

      // Verify server-side; success returns a fully verified access token.
      let verifyData: any;
      if (launch) {
        const verifyRes = await verifyBiometricLogin({
          variables: {
            input: {
              deviceId: capability.deviceId,
              challenge,
              signature: signed.signature,
              useragent: navigator.userAgent,
            },
          },
        });
        verifyData = verifyRes?.data?.verifyBiometricLogin;
      } else {
        const verifyRes = await verifyBiometricAssertion({
          variables: {
            input: {
              deviceId: capability.deviceId,
              challenge,
              signature: signed.signature,
            },
          },
        });
        verifyData = verifyRes?.data?.verifyBiometricAssertion;
      }

      // `loginResult` is only present once the backend returns the full login
      // payload (see note below). Until then only `accessToken` is available.
      const loginResult = verifyData?.loginResult;
      const accessToken = loginResult?.accessToken || verifyData?.accessToken;
      if (accessToken && accessToken.length > 0) {
        const t = jwt.decode(accessToken) as MFATypes.Token;
        if (t && t.otpVerified) {
          if (loginResult) {
            // Launch bypass (no prior password login): hydrate ALL local auth
            // state (username, providers, wards, ...) from the login payload.
            authService.persistAllValues(loginResult);
          } else {
            // Token-only path. This is safe only when local auth state already
            // exists (e.g. the MFA page after a password login). Without the
            // full login payload we cannot reconstruct username/providers/wards
            // for a cold launch, so bail rather than land on a broken app.
            if (!authService.getUsername()) {
              return false;
            }
            authService.clearToken();
            authService.persistToken(accessToken);
          }
          const from = destination || authService.getLastViewed() || "/patients";
          authService.clearLastViewed();
          // Flip the in-app auth state so protected routes (e.g. /patients)
          // admit the navigation. Route protection reads a React `isAuth` state
          // driven ONLY by this Apollo-cache write — persistAllValues alone
          // (localStorage/sessionStorage) does not update it. Every other login
          // path writes SET_AUTH here; without it ProtectedRoute bounces the
          // user back to /login and LoginPage stays stuck on "Verifying...".
          client.cache.writeQuery({
            query: SET_AUTH,
            data: { auth: { isAuth: true, __typename: "Auth" } },
          });
          // Persist site options before navigating. Unlike every other login
          // path (password/SSO/delegate/reset), the biometric launch never
          // fetched site options, so SITE_OPTIONS localStorage was left empty
          // (persistAllValues clears it). Pages that read site options at render
          // time then throw — e.g. PatientDetailsPage reads
          // getSiteOption(SHARE_TO_FEATURE).value.oda and crashes to a blank
          // page. Fetch + set them here (awaited; it's a quick query).
          try {
            const optsRes = await getOptions({});
            if (optsRes?.data?.getOptions) {
              authService.setSiteOptions(optsRes.data.getOptions);
            }
          } catch (e: any) {
            console.warn("[biometric] verify: site options fetch failed (non-blocking):", e?.message);
          }
          // Persist CPD providers before navigating. The CPD Portal route guard
          // (ProtectedRoute.checkCPDprogramAccess) redirects away from
          // /cpd-programs when authService.getCpdProviders() is empty, so this
          // list must be in localStorage before the user can reach that page.
          // persistAllValues clears it, and the heavy storeMobileDeviceToken
          // (which also sets it) is fire-and-forget below, so fetch + set it
          // here. This single query is quick; awaiting it won't strand the user.
          try {
            const cpdRes = await cpdProviders();
            if (cpdRes?.data?.getCpdProviders) {
              authService.setCpdProviders(cpdRes.data.getCpdProviders);
            }
          } catch (e: any) {
            console.warn("[biometric] verify: cpdProviders fetch failed (non-blocking):", e?.message);
          }
          // Best-effort device-token registration. Fire-and-forget: on a cold
          // launch these GraphQL calls can stall (auth context is still settling
          // right after persistAllValues), and awaiting them here would strand
          // the user on the "Verifying..." screen forever even though the login
          // already succeeded. Navigate immediately; let the store run in the
          // background and swallow its own errors.
          void storeMobileDeviceToken(updateUsersDeviceConfig, cpdProviders).catch((e) => {
            console.warn("[biometric] verify: storeMobileDeviceToken failed (non-blocking):", e?.message);
          });
          navigate(from);
          return true;
        }
      }

      // A cold-launch verify that produced no usable token almost always means
      // the server no longer has an active key for this device (revoked, or the
      // table was reset). Clear the stale local "enabled" flag so we stop
      // auto-attempting the fast-path and re-offer enrolment on the patient
      // list after the user logs in manually.
      if (launch) {
        const staleUser = authService.getBiometricUsername() || authService.getRememberMe();
        if (staleUser) {
          authService.setBiometricEnabled(staleUser, false);
          authService.setBiometricPrompted(staleUser, false);
        }
      }
      return false;
    } catch (e) {
      console.error("useBiometric.verify failed", e);
      return false;
    } finally {
      setVerifying(false);
    }
  };

  /**
   * When the user opts out of biometrics at launch ("Use Password" / "Use
   * another method") we suppress further auto-verify attempts for the rest of
   * this session, so the MFA page doesn't prompt again during the same login.
   * Session-scoped so a fresh app relaunch re-enables the fast-path.
   */
  const SUPPRESS_KEY = "biometric-suppressed";
  const suppressForSession = (): void => {
    sessionStorage.setItem(SUPPRESS_KEY, "true");
  };
  const clearSuppressForSession = (): void => {
    sessionStorage.removeItem(SUPPRESS_KEY);
  };
  const isSuppressedForSession = (): boolean => sessionStorage.getItem(SUPPRESS_KEY) === "true";

  /** Whether a returning-user biometric verification should be attempted. */
  const canVerify = (): boolean => {
    if (!mobility) return false;
    // Biometrics are not available to SSO/Cognito sign-ins, so never attempt
    // the returning-user fast-path for them (an SSO user cannot have enrolled a
    // device anyway; this makes the intent explicit).
    if (authService.isCognito()) return false;
    if (isSuppressedForSession()) return false;
    // On a cold launch clearAll() has wiped USERNAME, so fall back to the
    // remembered username and finally the last account that enrolled biometrics
    // on this device (persisted independently of "Remember me").
    const username =
      authService.getUsername() || authService.getRememberMe() || authService.getBiometricUsername();
    return !!username && authService.getBiometricEnabled(username);
  };

  return {
    mobility,
    isDesktop,
    // enable-offer prompt state (for DialogBoxBiometric)
    promptOpen,
    methodName,
    loading,
    disabling,
    verifying,
    // enrolment-success confirmation state (for DialogBoxBiometricSuccess)
    successOpen,
    dismissSuccess,
    // actions
    refreshMethodName,
    offerEnable,
    openEnrolPrompt,
    enable,
    decline,
    disable,
    verify,
    canVerify,
    suppressForSession,
    clearSuppressForSession,
    setPromptOpen,
  };
};

export default useBiometric;
